import type { CurrentShift, Locale } from '@saniplus/contracts';
import { MapPin, Readout, decorativeIcon } from '@saniplus/ui';
import { useTranslation } from 'react-i18next';

import { clockParts, crossesMidnight, longDate, relativeDay } from '../lib/format';

/**
 * THE SHIFT, AS A FOOTER — not as a second card.
 *
 * The home screen used to draw the state in one card and the shift in
 * another, one above the other, each with its own eyebrow, border and
 * padding. On a 390 px phone that is two panels, two headings and roughly
 * 230 px of chrome to answer two questions a worker asks in the same glance:
 * *am I on the clock, and which shift is that?* Nesting a card in a card is
 * forbidden (`DESIGN.md`, Don't), so the shift becomes the panel's own
 * footer: one hairline, two lines, no heading of its own.
 *
 * Two rules survive the compression intact:
 *
 * - **D-07 out loud.** An overnight shift belongs to its *start*
 *   `business_date`, so a 22:00→06:00 shift says « 27 août — se termine
 *   demain » rather than leaving a worker to work out which night they are
 *   paid for.
 * - **Correction C-14, on the phone too.** The date is the date. This
 *   contractor runs 08:00–16:00 day crews, and the shipped screen labelled
 *   one of them « Quart de ce soir » — a small lie a day crew catches
 *   immediately. « Nuit du … » appears only when the shift actually crosses
 *   midnight, which is a measured fact rather than a habit.
 */
interface ShiftLineProps {
  readonly shift: CurrentShift;
  readonly locale: Locale;
  /** Overrides the site — an open session on a shift the context no longer reports. */
  readonly site?: { readonly name: string };
  /** Overrides the zone the same way. `null` renders « Aucune zone ». */
  readonly zone?: { readonly name: string } | null;
  /**
   * Drop the start time, because the panel's own readout already IS it.
   *
   * Off duty, the one number a worker wants is when the shift starts, so the
   * instrument spends its 40 px on exactly that — and printing « 08 h 00 »
   * again ten pixels below at 25 px is the same fact twice at two sizes, which
   * is noise on a screen read in thirty seconds. The line then says what the
   * readout cannot: when it ends.
   */
  readonly omitStart?: boolean;
  /**
   * `headline` — this line IS the panel's answer, not a footnote to it.
   *
   * When there is no shift today the screen's whole job is "when am I next
   * due?", and the honest answer is a DAY, not a time. Printing « 10 h 12 » as
   * the panel's one big number said *ten past ten* with nothing anywhere near
   * it saying *which day* — the reader had to find the date three lines below,
   * attached to the end of the window. Headline puts the day first and at
   * title weight, with the window under it, so the two are read together.
   */
  readonly emphasis?: 'normal' | 'headline';
  /**
   * "Now" on the server's clock, so *today* and *tomorrow* are measured
   * against the same instant the rest of the screen is.
   */
  readonly now: number;
  readonly className?: string;
}

export function ShiftLine({
  shift,
  locale,
  site,
  zone,
  omitStart = false,
  emphasis = 'normal',
  now,
  className,
}: ShiftLineProps) {
  const { t } = useTranslation('punch');
  const tz = shift.site.timezone;
  const start = clockParts(shift.startsAt, tz);
  const end = clockParts(shift.endsAt, tz);
  const overnight = crossesMidnight(shift.startsAt, shift.endsAt, tz);
  const date = longDate(shift.startsAt, tz, locale);
  const zoneName = zone === undefined ? shift.zone : zone;

  /*
   * « Demain, vendredi 28 août » — the relative word AND the date, never one
   * alone. *Demain* answers the question a worker actually has and is true for
   * a few hours; the date is true whenever they read it, and it is the one a
   * supervisor will ask about. Beyond tomorrow there is no word worth
   * inventing: *dans 4 jours* is arithmetic the weekday already carries.
   */
  const relative = relativeDay(shift.startsAt, tz, now);
  const relativeWord =
    relative === 'today'
      ? t('shift.today')
      : relative === 'tomorrow'
        ? t('shift.tomorrow')
        : relative === 'yesterday'
          ? t('shift.yesterday')
          : null;

  const dayText =
    relativeWord === null
      ? emphasis === 'headline'
        ? date
        : t('shift.date', { date })
      : t('shift.dateRelative', { relative: relativeWord, date });

  /*
   * D-07's overnight note is relative to the SHIFT, not to the reader.
   *
   * « se termine demain » is only true of a shift that starts today. Said of
   * one starting tomorrow it is off by a day — and the whole point of putting
   * *demain* on the day line is that a worker now reads these two phrases
   * together. A shift on any other day says « se termine le lendemain », which
   * is a fact about the shift and cannot drift.
   */
  const endNote = !overnight
    ? null
    : relative === 'today'
      ? t('shift.endsTomorrow')
      : t('shift.endsNextDay');

  const dateSentence =
    endNote === null ? dayText : t('shift.dateWithEnd', { date: dayText, end: endNote });

  const place = (
    <p className="mt-1.5 flex items-start gap-2">
      <span aria-hidden="true" className="mt-0.5 shrink-0 text-ink-muted">
        <MapPin {...decorativeIcon('sm')} />
      </span>
      <span className="min-w-0 text-body text-ink">
        {site?.name ?? shift.site.name}
        <span aria-hidden="true" className="text-ink-muted">
          {' · '}
        </span>
        <span className="text-ink-muted">
          {zoneName === null ? t('shift.noZone') : zoneName.name}
        </span>
      </span>
    </p>
  );

  if (emphasis === 'headline') {
    return (
      <div className={className}>
        {/*
          * The day, in prose and at title weight, ABOVE the window.
          *
          * `readout-2` (40 px) was the obvious size for the window here and is
          * the wrong one: « 10 h 12 → 18 h 00 » set in 40 px mono measures
          * ~376 px and this card is 334 px wide at the reference phone, so it
          * wrapped the arrow onto its own line. The window keeps `detail`
          * (25 px) and the emphasis is spent on the day instead — which is the
          * fact that was missing, not the one that was too small.
          */}
        <p className="text-title-2 text-ink first-letter:uppercase">{dayText}</p>
        <p className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <Readout scale="detail" dateTime={shift.startsAt}>
            {start === null ? '' : t('format.clock', start)}
          </Readout>
          <span aria-hidden="true" className="text-ink-muted">
            {t('format.arrow')}
          </span>
          <Readout scale="detail" dateTime={shift.endsAt}>
            {end === null ? '' : t('format.clock', end)}
          </Readout>
          {endNote === null ? null : (
            <span className="text-body text-ink-muted">{endNote}</span>
          )}
        </p>
        {place}
      </div>
    );
  }

  return (
    <div className={className}>
      {/*
        * The window first, because it is the fact a worker checks against the
        * clock in the rail. `detail` (25 px) rather than the old panel's
        * `readout-2`: the panel above already spends the screen's one big
        * number, and two readouts competing is what made the shipped screen
        * read as two screens stacked.
        */}
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {omitStart ? (
          <span className="text-body text-ink-muted">{t('shift.until')}</span>
        ) : (
          <>
            <Readout scale="detail" dateTime={shift.startsAt}>
              {start === null ? '' : t('format.clock', start)}
            </Readout>
            <span aria-hidden="true" className="text-ink-muted">
              {t('format.arrow')}
            </span>
          </>
        )}
        <Readout scale="detail" dateTime={shift.endsAt}>
          {end === null ? '' : t('format.clock', end)}
        </Readout>
        <span className="text-body text-ink-muted">{dateSentence}</span>
      </p>

      {place}
    </div>
  );
}
